import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { ApiError } from '../api/client';
import {
  IconAlert,
  IconCheck,
  IconClose,
  IconCopy,
  IconInfo,
} from './icons';

/* ==========================================================================
   Buttons
   ========================================================================== */

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  block?: boolean;
  icon?: ReactNode;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  block = false,
  icon,
  children,
  className = '',
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      type="button"
      className={[
        'btn',
        `btn--${variant}`,
        size !== 'md' ? `btn--${size}` : '',
        block ? 'btn--block' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      // A loading button must not be clickable twice; disabling it is the
      // simplest correct guard against a double submit.
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <span className="spinner" /> : icon}
      {children}
    </button>
  );
}

/* ==========================================================================
   Form fields
   ========================================================================== */

interface FieldShellProps {
  label: string;
  hint?: string;
  error?: string;
  optional?: boolean;
  children: (props: { id: string; describedBy?: string; invalid: boolean }) => ReactNode;
}

/**
 * Wires label, hint and error to the control with real ids, so a screen
 * reader announces the problem along with the field instead of leaving a red
 * border to carry the whole message.
 */
export function FieldShell({ label, hint, error, optional, children }: FieldShellProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
        {optional && <span className="field__optional">optional</span>}
      </label>
      {hint && (
        <p className="field__hint" id={hintId}>
          {hint}
        </p>
      )}
      {children({ id, describedBy: describedBy || undefined, invalid: Boolean(error) })}
      {error && (
        <p className="field__error" id={errorId} role="alert">
          <IconAlert size={14} />
          <span>{error}</span>
        </p>
      )}
    </div>
  );
}

type TextFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> & {
  label: string;
  hint?: string;
  error?: string;
  optional?: boolean;
  mono?: boolean;
};

export function TextField({ label, hint, error, optional, mono, ...rest }: TextFieldProps) {
  return (
    <FieldShell label={label} hint={hint} error={error} optional={optional}>
      {({ id, describedBy, invalid }) => (
        <input
          id={id}
          className={`input${mono ? ' input--mono' : ''}`}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          {...rest}
        />
      )}
    </FieldShell>
  );
}

type SelectFieldProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> & {
  label: string;
  hint?: string;
  error?: string;
};

export function SelectField({ label, hint, error, children, ...rest }: SelectFieldProps) {
  return (
    <FieldShell label={label} hint={hint} error={error}>
      {({ id, describedBy, invalid }) => (
        <select
          id={id}
          className="select"
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          {...rest}
        >
          {children}
        </select>
      )}
    </FieldShell>
  );
}

type TextAreaFieldProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> & {
  label: string;
  hint?: string;
  error?: string;
  optional?: boolean;
};

export function TextAreaField({ label, hint, error, optional, ...rest }: TextAreaFieldProps) {
  return (
    <FieldShell label={label} hint={hint} error={error} optional={optional}>
      {({ id, describedBy, invalid }) => (
        <textarea
          id={id}
          className="textarea"
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          {...rest}
        />
      )}
    </FieldShell>
  );
}

interface CheckLineProps {
  title: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

export function CheckLine({ title, description, checked, onChange, disabled }: CheckLineProps) {
  return (
    <label className="checkline">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.currentTarget.checked)}
      />
      <span className="checkline__text">
        <span className="checkline__title">{title}</span>
        {description}
      </span>
    </label>
  );
}

/* ==========================================================================
   Callouts
   ========================================================================== */

export function Callout({
  tone = 'info',
  title,
  children,
}: {
  tone?: 'info' | 'warn' | 'danger';
  title?: string;
  children: ReactNode;
}) {
  const Icon = tone === 'info' ? IconInfo : IconAlert;
  return (
    <div className={`callout callout--${tone}`}>
      <Icon size={16} />
      <div>
        {title && <span className="callout__title">{title}</span>}
        {children}
      </div>
    </div>
  );
}

/**
 * Renders an error from the API using the server's own wording. The console
 * does not paraphrase — the backend already explained the problem, and a
 * second phrasing is a second thing to keep in sync.
 */
export function ErrorNote({ error }: { error: unknown }) {
  // Forms put this above their fields, so in a dialog scrolled down to its
  // submit button the error would appear out of sight and the click would
  // seem to do nothing. Bring it into view whenever a new error arrives.
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (error) ref.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [error]);

  if (!error) return null;

  const message =
    error instanceof ApiError
      ? error.message
      : error instanceof Error
        ? error.message
        : 'Something went wrong.';

  const problems = error instanceof ApiError ? error.fieldProblems : [];

  return (
    <div ref={ref}>
      <Callout tone="danger" title="That didn’t work">
        {message}
        {problems.length > 0 && (
          <ul style={{ margin: 'var(--s2) 0 0', paddingLeft: 'var(--s4)' }}>
            {problems.map((problem) => (
              <li key={problem.path}>
                <code className="mono">{problem.path}</code> — {problem.message}
              </li>
            ))}
          </ul>
        )}
      </Callout>
    </div>
  );
}

/* ==========================================================================
   Dialog — native <dialog>, so it escapes clipping ancestors and gets focus
   trapping plus Esc-to-close from the platform.
   ========================================================================== */

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  hint?: string;
  wide?: boolean;
  children: ReactNode;
}

export function Dialog({ open, onClose, title, hint, wide, children }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (open && !node.open) node.showModal();
    if (!open && node.open) node.close();
  }, [open]);

  if (!open) return null;

  return (
    <dialog
      ref={ref}
      className={`dialog${wide ? ' dialog--wide' : ''}`}
      // Esc fires `cancel`; without this the element closes itself and React
      // state still believes it is open.
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClose={onClose}
    >
      <div className="dialog__head">
        <div>
          <h2 className="dialog__title">{title}</h2>
          {hint && <p className="dialog__hint">{hint}</p>}
        </div>
        <button type="button" className="dialog__close" onClick={onClose} aria-label="Close">
          <IconClose size={16} />
        </button>
      </div>
      <div className="dialog__body scroll-area">{children}</div>
    </dialog>
  );
}

/**
 * Destructive confirmation. Requires typing the exact subject name when
 * `confirmWord` is given: revoking a license or deactivating a company locks
 * out every user at that company, so it should cost more than one click.
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  body,
  confirmLabel,
  confirmWord,
  loading,
  error,
  extra,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  body: ReactNode;
  confirmLabel: string;
  confirmWord?: string;
  loading?: boolean;
  error?: unknown;
  /** Additional controls, shown between the warning and the confirm field. */
  extra?: ReactNode;
}) {
  const [typed, setTyped] = useState('');

  useEffect(() => {
    if (open) setTyped('');
  }, [open]);

  const ready = !confirmWord || typed.trim() === confirmWord;

  return (
    <Dialog open={open} onClose={onClose} title={title}>
      <div className="stack">
        <Callout tone="danger">{body}</Callout>
        {extra}
        {error ? <ErrorNote error={error} /> : null}
        {confirmWord && (
          <TextField
            label={`Type “${confirmWord}” to confirm`}
            value={typed}
            autoComplete="off"
            spellCheck={false}
            onChange={(event) => setTyped(event.currentTarget.value)}
          />
        )}
        <div className="form-actions">
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="danger" disabled={!ready} loading={loading} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

/* ==========================================================================
   Empty / loading / secret
   ========================================================================== */

export function EmptyState({
  icon,
  title,
  body,
  action,
}: {
  icon?: ReactNode;
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      {icon}
      <p className="empty__title">{title}</p>
      {/* Empty states teach the next action rather than reporting absence. */}
      <p className="empty__body">{body}</p>
      {action && <div className="empty__actions">{action}</div>}
    </div>
  );
}

export function SkeletonRows({ rows = 5 }: { rows?: number }) {
  return (
    <div className="skeleton-rows" aria-hidden>
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="row" style={{ gap: 'var(--s5)' }}>
          <div className="skeleton" style={{ width: '28%' }} />
          <div className="skeleton" style={{ width: '18%' }} />
          <div className="skeleton grow" />
        </div>
      ))}
    </div>
  );
}

/** A value the server will never show again. Copy it or lose it. */
export function SecretReveal({ label, value }: { label: string; value: string }) {
  const { push } = useToast();

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      push({ tone: 'success', title: `${label} copied` });
    } catch {
      push({
        tone: 'error',
        title: 'Could not copy',
        body: 'Select the value and copy it manually.',
      });
    }
  };

  return (
    <div className="secret">
      <span className="field__label">{label}</span>
      <div className="secret__row">
        <code className="secret__value scroll-area">{value}</code>
        <Button size="sm" icon={<IconCopy />} onClick={copy}>
          Copy
        </Button>
      </div>
    </div>
  );
}

export function CopyButton({ value, label = 'Copied' }: { value: string; label?: string }) {
  const { push } = useToast();
  return (
    <button
      type="button"
      className="icon-btn"
      aria-label={`Copy ${label}`}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          push({ tone: 'success', title: label });
        } catch {
          push({ tone: 'error', title: 'Could not copy to the clipboard' });
        }
      }}
    >
      <IconCopy />
    </button>
  );
}

/* ==========================================================================
   Toasts
   ========================================================================== */

interface Toast {
  id: number;
  tone: 'success' | 'error' | 'info';
  title: string;
  body?: string;
}

const ToastContext = createContext<{ push: (toast: Omit<Toast, 'id'>) => void } | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const push = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = nextId.current++;
    setToasts((current) => [...current, { ...toast, id }]);
    // Errors linger; confirmations get out of the way.
    window.setTimeout(() => {
      setToasts((current) => current.filter((entry) => entry.id !== id));
    }, toast.tone === 'error' ? 8000 : 4000);
  }, []);

  const value = useMemo(() => ({ push }), [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* aria-live so a confirmation is announced, not only seen. */}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast toast--${toast.tone}`}>
            {toast.tone === 'success' ? (
              <IconCheck size={16} />
            ) : toast.tone === 'error' ? (
              <IconAlert size={16} />
            ) : (
              <IconInfo size={16} />
            )}
            <div className="toast__text">
              <div className="toast__title">{toast.title}</div>
              {toast.body && <div className="toast__body">{toast.body}</div>}
            </div>
            <button
              type="button"
              className="icon-btn"
              aria-label="Dismiss"
              onClick={() => setToasts((current) => current.filter((e) => e.id !== toast.id))}
            >
              <IconClose size={14} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used inside ToastProvider');
  return context;
}
